import { useState, useEffect } from 'react';
import { Trash } from 'lucide-react';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL || '';

export default function MyBusiness() {
  const [activeTab, setActiveTab] = useState('business');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [business, setBusiness] = useState({
    name: '',
    logo: null,
    address: '',
    city: '',
    zip_code: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    slug: '',
    tax_id: '',
    registration_number: '',
    bank_name: '',
    bank_account: '',
    iban: '',
    swift: '',
    currency: 'MKD',
    tax_rate: 18.0,
    invoice_prefix: '000',
    invoice_due_days: 15,
    invoice_notes: '',
  });
  
  // Fetch business settings on component mount
  useEffect(() => {
    const fetchBusinessSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setError(t('Потребна е најава'));
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${apiUrl}/business-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          // If 404, we'll just use default settings
          if (response.status === 404) {
            setLoading(false);
            return;
          }
          throw new Error(t('Неуспешно вчитување на поставките за бизнисот'));
        }
        
        const data = await response.json();
        
        // Map API response to component state
        setBusiness({
          name: data.name || '',
          logo: data.logo || null,
          address: data.address || '',
          city: data.city || '',
          zip_code: data.zip_code || '',
          country: data.country || '',
          phone: data.phone || '',
          email: data.email || '',
          website: data.website || '',
          slug: data.slug || '',
          tax_id: data.tax_id || '',
          registration_number: data.registration_number || '',
          bank_name: data.bank_name || '',
          bank_account: data.bank_account || '',
          iban: data.iban || '',
          swift: data.swift || '',
          currency: data.currency || 'MKD',
          tax_rate: data.tax_rate || 18.0,
          invoice_prefix: data.invoice_prefix || 'INV-',
          invoice_due_days: data.invoice_due_days || 15,
          invoice_notes: data.invoice_notes || '',
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinessSettings();
  }, []);
  
  // Save business settings
  const saveBusinessSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/business-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(business)
      });
      
      if (!response.ok) {
        throw new Error(t('Неуспешно зачувување на поставките за бизнисот'));
      }
      
      await response.json(); // Result contains message but we don't need to use it
      setSuccess(t('Поставките за бизнисот се успешно зачувани'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users from database
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        return;
      }
      
      const response = await fetch(`${apiUrl}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(t('Неуспешно вчитување на корисниците'));
      }
      
      const data = await response.json();
      const mappedUsers = data.map(user => ({
        id: user.id,
        name: user.username,
        email: user.email,
        role: user.role || '',
        occupier: user.occupier
      }));
      setUsers(mappedUsers);
      
      // Set current occupier from the first user, or default to 1
      if (mappedUsers.length > 0) {
        setCurrentOccupier(mappedUsers[0].occupier);
      }
      setUsersLoaded(true);
    } catch (err) {
      setError(err.message);
      // Set empty user for adding new ones if fetch fails
      setUsers([{ id: Date.now(), name: '', email: '', role: '', occupier: currentOccupier }]);
      setUsersLoaded(true);
    }
  };

  // Save users to database
  const saveUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/users/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ users })
      });

      if (!response.ok) {
        throw new Error(t('Неуспешно зачувување на корисниците'));
      }

      const result = await response.json();
      setSuccess(t('Корисниците се успешно зачувани'));
      
      // Refresh users list to get updated IDs
      await fetchUsers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [currentOccupier, setCurrentOccupier] = useState(1);

  const handleBusinessChange = (field, value) => {
    setBusiness(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${apiUrl}/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || t('Неуспешно прикачување на логото'));
      }

      const result = await response.json();
      handleBusinessChange('logo', result.file.path);
      // Let the sidebar (Menu.js) swap in the new logo without a reload
      window.dispatchEvent(new Event('business-logo-updated'));
      setSuccess(t('Логото е успешно прикачено'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Logos are stored as API-relative paths (/storage/...); older values may be data URLs
  const logoSrc = business.logo && business.logo.startsWith('/storage/')
    ? `${apiUrl}${business.logo}`
    : business.logo;

  const handleUserChange = (index, field, value) => {
    const updated = [...users];
    updated[index][field] = value;
    setUsers(updated);
  };

  const addUser = () => {
    setUsers([...users, { id: Date.now(), name: '', email: '', role: '', occupier: currentOccupier }]);
  };

  const removeUser = (id) => {
    setUsers(users.filter(user => user.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Alert Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">{t('Грешка!')}</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">{t('Успешно!')}</strong>
          <span className="block sm:inline"> {success}</span>
        </div>
      )}
      
      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('Вчитување...')}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-4 border-b pb-2">
        <button
          onClick={() => setActiveTab('business')}
          className={`px-4 py-2 font-medium ${activeTab === 'business' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-600'}`}
        >
          {t('Податоци за бизнисот')}
        </button>
        <button
          onClick={() => setActiveTab('invoicing')}
          className={`px-4 py-2 font-medium ${activeTab === 'invoicing' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-600'}`}
        >
          {t('Подесувања за фактурирање')}
        </button>
        <button
          onClick={() => {
            setActiveTab('users');
            if (!usersLoaded) {
              fetchUsers();
            }
          }}
          className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-600'}`}
        >
          {t('Корисници')}
        </button>
      </div>

      {/* Business Info Tab */}
      {activeTab === 'business' && (
        <>
          {/* Business Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('Име на бизнисот')}</label>
              <input
                type="text"
                value={business.name}
                onChange={(e) => handleBusinessChange('name', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Име')}
                title={t('Име')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('Лого')}</label>

              {/* Hidden actual file input */}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                id="logo-upload"
                className="hidden"
              />

              {/* Styled label as button */}
              <label
                htmlFor="logo-upload"
                className="inline-block cursor-pointer bg-yellow-500 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm select-none"
              >
                {t('Прикачи лого')}
              </label>

              {/* Display selected logo */}
              {business.logo && (
                <img
                  src={logoSrc}
                  alt={t('Лого')}
                  className="h-20 object-contain border rounded mt-2"
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={business.address}
                onChange={(e) => handleBusinessChange('address', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Адреса')}
                title={t('Адреса')}
              />
              <input
                type="text"
                value={business.city}
                onChange={(e) => handleBusinessChange('city', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Град')}
                title={t('Град')}
              />
              <input
                type="text"
                value={business.zip_code}
                onChange={(e) => handleBusinessChange('zip_code', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Поштенски број')}
                title={t('Поштенски број')}
              />
              <input
                type="text"
                value={business.country}
                onChange={(e) => handleBusinessChange('country', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Држава')}
                title={t('Држава')}
              />
              <input
                type="tel"
                value={business.phone}
                onChange={(e) => handleBusinessChange('phone', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Телефон')}
                title={t('Телефон')}
              />
              <input
                type="email"
                value={business.email}
                onChange={(e) => handleBusinessChange('email', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Е-пошта')}
                title={t('Е-пошта')}
              />
              <input
                type="text"
                value={business.website}
                onChange={(e) => handleBusinessChange('website', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Веб-страница')}
                title={t('Веб-страница')}
              />
              <input
                type="text"
                value={business.slug}
                onChange={(e) => handleBusinessChange('slug', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Кратко име (slug)')}
                title={t('Кратко име (slug)')}
              />
            </div>
          </div>

          {/* Registration Info Section */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg space-y-4">
            <h2 className="text-md font-semibold text-gray-700">{t('Податоци за регистрација')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={business.tax_id}
                onChange={(e) => handleBusinessChange('tax_id', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('ЕДБ / ДДВ број')}
                title={t('ЕДБ / ДДВ број')}
              />
              <input
                type="text"
                value={business.registration_number}
                onChange={(e) => handleBusinessChange('registration_number', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('ЕМБС')}
              />
            </div>
          </div>

          {/* Bank Info Section */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg space-y-4">
            <h2 className="text-md font-semibold text-gray-700">{t('Банкарски податоци')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={business.bank_name}
                onChange={(e) => handleBusinessChange('bank_name', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Име на банка')}
              />
              <input
                type="text"
                value={business.bank_account}
                onChange={(e) => handleBusinessChange('bank_account', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Жиро сметка')}
              />
              <input
                type="text"
                value={business.iban}
                onChange={(e) => handleBusinessChange('iban', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder="IBAN"
              />
              <input
                type="text"
                value={business.swift}
                onChange={(e) => handleBusinessChange('swift', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('SWIFT / BIC код')}
              />
            </div>
          </div>
        </>
      )}

      {/* Invoicing Settings Tab */}
      {activeTab === 'invoicing' && (
        <div className="space-y-4">
          <h2 className="text-md font-semibold text-gray-700">{t('Подесувања за фактури')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('Валута')}</label>
              <input
                type="text"
                value={business.currency}
                onChange={(e) => handleBusinessChange('currency', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Валута (пр. MKD, EUR, USD)')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('Стандардна стапка на ДДВ (%)')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={business.tax_rate}
                onChange={(e) => handleBusinessChange('tax_rate', parseFloat(e.target.value))}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Стапка на ДДВ (пр. 18.00)')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('Префикс на број на фактура')}</label>
              <input
                type="text"
                value={business.invoice_prefix}
                onChange={(e) => handleBusinessChange('invoice_prefix', e.target.value)}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Префикс на фактура (пр. INV-)')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('Стандарден рок за плаќање (денови)')}</label>
              <input
                type="number"
                min="0"
                value={business.invoice_due_days}
                onChange={(e) => handleBusinessChange('invoice_due_days', parseInt(e.target.value, 10))}
                className="w-full border p-2 rounded text-sm"
                placeholder={t('Рок за плаќање во денови (пр. 15)')}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('Стандардни забелешки на фактура')}</label>
            <textarea
              value={business.invoice_notes}
              onChange={(e) => handleBusinessChange('invoice_notes', e.target.value)}
              className="w-full border p-2 rounded text-sm"
              placeholder={t('Забелешки што ќе се прикажуваат на фактурите')}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* <h2 className="text-md font-semibold text-gray-700">Users</h2> */}
          <table className="w-full table-auto border border-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">#</th>
                <th className="border p-2">{t('Име')}</th>
                <th className="border p-2">{t('Е-пошта')}</th>
                <th className="border p-2">{t('Улога')}</th>
                <th className="border p-2">{t('Акции')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id}>
                  <td className="border p-2 text-center font-medium text-gray-600">
                    {index + 1}
                  </td>
                  <td className="border p-2">
                    <input
                      type="text"
                      value={user.name}
                      onChange={(e) => handleUserChange(index, 'name', e.target.value)}
                      className="w-full p-1 border rounded"
                      placeholder={t('Име')}
                    />
                  </td>
                  <td className="border p-2">
                    <input
                      type="email"
                      value={user.email}
                      onChange={(e) => handleUserChange(index, 'email', e.target.value)}
                      className="w-full p-1 border rounded"
                      placeholder={t('Е-пошта')}
                    />
                  </td>
                  <td className="border p-2">
                    <select
                      value={user.role}
                      onChange={(e) => handleUserChange(index, 'role', e.target.value)}
                      className="w-full p-1 border rounded"
                    >
                      <option value="employee">{t('вработен')}</option>
                      <option value="admin">{t('администратор')}</option>
                    </select>
                  </td>
                  <td className="border p-2 text-center">
                    <button
                      onClick={() => removeUser(user.id)}
                      title={user.role === 'admin' ? t('Администраторот не може да се избрише') : t('Отстрани')}
                      disabled={user.role === 'admin'}
                      className={`${
                        user.role === 'admin' 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-red-600 hover:text-red-900'
                      }`}
                    >
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addUser}
            className="px-4 py-2 bg-orange-500 text-white text-sm rounded hover:bg-orange-600"
          >
            {t('Додади корисник')}
          </button>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={activeTab === 'users' ? saveUsers : saveBusinessSettings}
          disabled={loading}
          className={`px-6 py-2 ${loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} text-white font-medium rounded`}
        >
          {loading ? t('Зачувување...') : (activeTab === 'users' ? t('Зачувај корисници') : t('Зачувај подесувања'))}
        </button>
      </div>
    </div>
  );
}
